import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { X, Server, Bug, AlertTriangle, ChevronRight } from "lucide-react";

// Slide-over that shows the exact records behind a dashboard number. Open it
// with a metric key; device rows link through to the agent detail page.

const SEV = {
  critical: "text-red-400", high: "text-orange-400", medium: "text-amber-400", low: "text-emerald-400",
};

export default function DrillPanel({ metric, client, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!metric) return;
    setData(null); setErr(false);
    api.get(`/exec/drill`, { params: { metric, ...(client ? { client } : {}) } })
      .then(r => setData(r.data)).catch(() => setErr(true));
  }, [metric, client]);

  if (!metric) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-md h-full bg-zinc-950 border-l border-zinc-800 shadow-2xl flex flex-col animate-in slide-in-from-right">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900">
          <div>
            <div className="text-sm font-medium text-zinc-100">{data?.title || "Loading…"}</div>
            {data && <div className="text-[11px] text-zinc-500 font-mono">{data.count} {data.kind}{data.count === 1 ? "" : "s"}</div>}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {err && <div className="text-sm text-zinc-500 p-4">Could not load.</div>}
          {data && data.count === 0 && <div className="text-sm text-zinc-600 p-4">Nothing here — everything is clear.</div>}
          {data && data.items.map((it, i) => {
            const clickable = it.kind === "device" && it.agent_id;
            const Icon = it.kind === "vuln" ? Bug : it.kind === "incident" ? AlertTriangle : Server;
            return (
              <div key={i}
                onClick={clickable ? () => { onClose(); nav(`/agents/${it.agent_id}`); } : undefined}
                className={`flex items-center gap-3 px-3 py-2.5 rounded border border-zinc-900 mb-1.5 ${clickable ? "hover:border-zinc-700 hover:bg-zinc-900/40 cursor-pointer" : ""}`}>
                <Icon size={15} className={it.severity ? SEV[it.severity] || "text-zinc-500" : "text-zinc-500"} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 truncate">
                    {it.label}
                    {/* An unassigned device is named by its hostname, which is
                        indistinguishable from a person called FPHLTDYBHY64.
                        Say so, so the gap is visible and fixable. */}
                    {it.kind === "device" && it.owned === false && (
                      <span className="ml-2 text-[10px] uppercase font-mono text-zinc-600">no owner</span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {it.kind === "device"
                      ? [it.hostname, it.os, it.detail].filter(Boolean).join(" · ")
                      : it.detail}
                  </div>
                </div>
                {it.severity && <span className={`text-[10px] uppercase font-mono ${SEV[it.severity] || "text-zinc-500"}`}>{it.severity}</span>}
                {it.status && it.kind === "device" && <span className={`text-[10px] uppercase font-mono ${it.status === "online" ? "text-emerald-400" : "text-zinc-600"}`}>{it.status}</span>}
                {clickable && <ChevronRight size={14} className="text-zinc-700" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
